import React, { useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AgentConfigSection from "../AgentConfigSection";
import {
  CATEGORY_COLORS,
  CATEGORY_COLOR_FALLBACK,
  TABLE_SCOPE_TOOL_NAMES,
} from "../toolCatalog";
import { resolveToolIcon } from "../../agentToolIcons";
import {
  SELF_CONFIGURATION_TOOL_NAMES,
  SELF_CONFIG_EDIT_TOOL_NAME,
  isSelfConfigurationEditEnabled,
} from "../selfConfiguration";

function getCategoryColor(title) {
  return CATEGORY_COLORS[title] || CATEGORY_COLOR_FALLBACK;
}

// Off / Ask segmented toggle. `value` is true when the action requires approval.
function ApprovalToggle({ value, onChange, disabled = false }) {
  const baseSx = {
    minWidth: 0,
    px: 1.75,
    py: 0.5,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    borderRadius: 0,
    textTransform: "none",
    boxShadow: "none",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        border: 1,
        borderColor: "divider",
        borderRadius: "6px",
        overflow: "hidden",
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Button
        type="button"
        disableElevation
        disabled={disabled}
        onClick={() => !value || onChange(false)}
        sx={{
          ...baseSx,
          color: !value ? "text.primary" : "text.secondary",
          backgroundColor: !value ? "action.selected" : "background.paper",
          "&:hover": {
            backgroundColor: !value ? "action.selected" : "action.hover",
          },
        }}
      >
        Off
      </Button>
      <Button
        type="button"
        disableElevation
        disabled={disabled}
        onClick={() => value || onChange(true)}
        sx={{
          ...baseSx,
          color: value ? "warning.main" : "text.secondary",
          backgroundColor: (theme) =>
            value ? alpha(theme.palette.warning.main, 0.15) : "background.paper",
          "&:hover": {
            backgroundColor: (theme) =>
              value
                ? alpha(theme.palette.warning.main, 0.22)
                : "action.hover",
          },
        }}
      >
        Ask
      </Button>
    </Box>
  );
}

function PolicyPanel({ title, count, action, children }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: "10px",
        overflow: "hidden",
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {count != null && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                color: "text.secondary",
                backgroundColor: "action.hover",
                px: 1,
                py: 0.25,
                borderRadius: "10px",
              }}
            >
              {count}
            </Box>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function PolicyRow({ icon, iconColor, name, meta, special, control }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: "none" },
        backgroundColor: (theme) =>
          special ? alpha(theme.palette.warning.main, 0.08) : "transparent",
        borderLeft: special ? 3 : 0,
        borderLeftColor: "warning.main",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            backgroundColor: (theme) => alpha(iconColor, 0.12),
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </Typography>
          {meta && (
            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}>
              {meta}
            </Typography>
          )}
        </Box>
      </Box>
      {control}
    </Box>
  );
}

export default function ApprovalPoliciesTab({
  enabledTools = [],
  toolCategories = [],
  selfConfigurationMode,
  approvalRequiredTools = [],
  setApprovalRequiredTools,
  onToggleToolApproval,
  scopeChangeRequiresApproval = true,
  setScopeChangeRequiresApproval,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyGated, setOnlyGated] = useState(false);

  const editEnabled = isSelfConfigurationEditEnabled(selfConfigurationMode);

  // Resolve metadata for every equipped, non-self-config tool from the catalog.
  const equippedTools = useMemo(() => {
    const enabledSet = new Set(enabledTools);
    const byName = new Map();
    toolCategories.forEach((category) => {
      (category?.tools || []).forEach((tool) => {
        if (!tool?.name || byName.has(tool.name)) return;
        byName.set(tool.name, {
          name: tool.name,
          label: tool.label || tool.name,
          iconName: tool.iconName || null,
          categoryTitle: category.title || "General",
          scoped: TABLE_SCOPE_TOOL_NAMES.has(tool.name),
        });
      });
    });
    return enabledTools
      .filter(
        (name) =>
          enabledSet.has(name) && !SELF_CONFIGURATION_TOOL_NAMES.has(name)
      )
      .map(
        (name) =>
          byName.get(name) || {
            name,
            label: name,
            iconName: null,
            categoryTitle: "General",
            scoped: false,
          }
      );
  }, [enabledTools, toolCategories]);

  const gatedCount = useMemo(
    () =>
      equippedTools.filter((tool) => approvalRequiredTools.includes(tool.name))
        .length,
    [equippedTools, approvalRequiredTools]
  );

  const visibleTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return equippedTools.filter((tool) => {
      if (onlyGated && !approvalRequiredTools.includes(tool.name)) return false;
      if (!query) return true;
      return `${tool.label} ${tool.name} ${tool.categoryTitle}`
        .toLowerCase()
        .includes(query);
    });
  }, [equippedTools, searchQuery, onlyGated, approvalRequiredTools]);

  const groupedTools = useMemo(() => {
    const groups = new Map();
    visibleTools.forEach((tool) => {
      const key = tool.categoryTitle || "General";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(tool);
    });
    return Array.from(groups.entries());
  }, [visibleTools]);

  const requireAll = () => {
    if (!setApprovalRequiredTools) return;
    setApprovalRequiredTools(equippedTools.map((tool) => tool.name));
  };

  const clearAll = () => {
    if (!setApprovalRequiredTools) return;
    setApprovalRequiredTools([]);
  };

  const handleToggle = (toolName, next) => {
    if (!onToggleToolApproval) return;
    const isGated = approvalRequiredTools.includes(toolName);
    if (next !== isGated) onToggleToolApproval(toolName);
  };

  return (
    <AgentConfigSection id="approvalPolicies">
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          p: 1.5,
          borderRadius: "8px",
          border: 1,
          borderColor: "divider",
          backgroundColor: "action.hover",
          mb: 2.5,
        }}
      >
        <GppGoodOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            Human-only.
          </Box>{" "}
          The agent cannot change these policies. When a gated action runs, the
          agent pauses and you approve or reject it inline in chat before it
          executes.
        </Typography>
      </Box>

      <PolicyPanel title="Configuration" count="1 policy">
        <PolicyRow
          special
          icon={
            <SettingsSuggestOutlinedIcon
              sx={{ fontSize: 20, color: "warning.main" }}
            />
          }
          iconColor="#f59e0b"
          name="Scope changes (self-configuration)"
          meta={
            editEnabled
              ? `When the agent calls ${SELF_CONFIG_EDIT_TOOL_NAME} with table, workflow, agent, or file scope fields`
              : "Only applies when self-configuration edit is enabled. This agent can't change its own loadout, so there's nothing to approve."
          }
          control={
            <ApprovalToggle
              value={editEnabled ? scopeChangeRequiresApproval : false}
              disabled={!editEnabled || !setScopeChangeRequiresApproval}
              onChange={(next) =>
                setScopeChangeRequiresApproval &&
                setScopeChangeRequiresApproval(next)
              }
            />
          }
        />
      </PolicyPanel>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.25,
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          className="org-styled-textfield"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search equipped tools..."
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
        <Chip
          label={`Requires approval (${gatedCount})`}
          size="small"
          variant={onlyGated ? "filled" : "outlined"}
          color={onlyGated ? "warning" : "default"}
          onClick={() => setOnlyGated((prev) => !prev)}
          sx={{ fontSize: 12, fontWeight: 500 }}
        />
        <Button
          size="small"
          variant="text"
          onClick={clearAll}
          disabled={gatedCount === 0}
          sx={{ fontSize: 12, fontWeight: 600, textTransform: "none" }}
        >
          Clear all gates
        </Button>
      </Box>

      {equippedTools.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderStyle: "dashed",
            borderRadius: "8px",
            p: 3,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <BuildOutlinedIcon sx={{ fontSize: 32, opacity: 0.5, mb: 1 }} />
          <Typography sx={{ fontSize: 13 }}>
            No tools equipped yet. Equip tools on the Tools tab first, then set
            which ones need approval.
          </Typography>
        </Box>
      ) : (
        <PolicyPanel
          title="Equipped tools"
          count={`${equippedTools.length} tools · ${gatedCount} gated`}
          action={
            <Button
              size="small"
              variant="text"
              onClick={requireAll}
              disabled={gatedCount === equippedTools.length}
              sx={{ fontSize: 12, fontWeight: 600, textTransform: "none" }}
            >
              Require approval for all
            </Button>
          }
        >
          {visibleTools.length === 0 ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No equipped tools match the current filter.
              </Typography>
            </Box>
          ) : (
            groupedTools.map(([categoryTitle, tools]) => {
              const color = getCategoryColor(categoryTitle);
              return tools.map((tool) => {
                const Icon = resolveToolIcon(tool.iconName);
                const gated = approvalRequiredTools.includes(tool.name);
                return (
                  <PolicyRow
                    key={tool.name}
                    icon={<Icon sx={{ fontSize: 18, color }} />}
                    iconColor={color}
                    name={tool.label}
                    meta={
                      tool.scoped
                        ? `${categoryTitle} · scoped`
                        : categoryTitle
                    }
                    control={
                      <ApprovalToggle
                        value={gated}
                        onChange={(next) => handleToggle(tool.name, next)}
                      />
                    }
                  />
                );
              });
            })
          )}
        </PolicyPanel>
      )}

      <Typography
        sx={{
          fontSize: 12,
          color: "text.disabled",
          mt: 1,
          pt: 1.5,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        Same data as the Tools tab — toggling here updates the detail-panel
        checkbox and tile badge. Unequipped tools are hidden; equip them on the
        Tools tab first.
      </Typography>
    </AgentConfigSection>
  );
}
